import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { auth, ApiError } from '../lib/api';
import Layout from '../components/Layout';

export default function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const changeMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      auth.changePassword(data.currentPassword, data.newPassword),
    onSuccess: () => {
      setSuccess('パスワードを変更しました');
      setError('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('パスワード変更に失敗しました');
      }
      setSuccess('');
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // バリデーション
    if (newPassword !== confirmPassword) {
      setError('新しいパスワードが一致しません');
      return;
    }

    if (newPassword.length < 8) {
      setError('パスワードは8文字以上必要です');
      return;
    }

    // パスワード強度チェック
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasLowercase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSymbol = /[!@#$%^&*]/.test(newPassword);

    if (!hasUppercase || !hasLowercase || !hasNumber || !hasSymbol) {
      setError('パスワードには大文字、小文字、数字、記号（!@#$%^&*）を含めてください');
      return;
    }

    changeMutation.mutate({ currentPassword, newPassword });
  };

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    if (!password) return { strength: 0, label: '', color: '' };

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*]/.test(password)) score++;

    if (score <= 2) return { strength: 25, label: '弱い', color: 'bg-red-500' };
    if (score <= 4) return { strength: 50, label: '普通', color: 'bg-yellow-500' };
    if (score <= 5) return { strength: 75, label: '強い', color: 'bg-blue-500' };
    return { strength: 100, label: 'とても強い', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="card">
          <h1 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            パスワード変更
          </h1>

          {success && (
            <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-600">
              {success}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 現在のパスワード */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                現在のパスワード *
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="input"
                required
                autoComplete="current-password"
              />
            </div>

            {/* 新しいパスワード */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                新しいパスワード *
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                required
                autoComplete="new-password"
              />
              {newPassword && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${passwordStrength.color}`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      />
                    </div>
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {passwordStrength.label}
                    </span>
                  </div>
                </div>
              )}
              <ul className="mt-2 text-xs space-y-1" style={{ color: 'var(--text-secondary)' }}>
                <li className={newPassword.length >= 8 ? 'text-green-500' : ''}>
                  ✓ 8文字以上
                </li>
                <li className={/[A-Z]/.test(newPassword) ? 'text-green-500' : ''}>
                  ✓ 大文字を含む
                </li>
                <li className={/[a-z]/.test(newPassword) ? 'text-green-500' : ''}>
                  ✓ 小文字を含む
                </li>
                <li className={/[0-9]/.test(newPassword) ? 'text-green-500' : ''}>
                  ✓ 数字を含む
                </li>
                <li className={/[!@#$%^&*]/.test(newPassword) ? 'text-green-500' : ''}>
                  ✓ 記号（!@#$%^&*）を含む
                </li>
              </ul>
            </div>

            {/* パスワード確認 */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                新しいパスワード（確認） *
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                required
                autoComplete="new-password"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500 mt-1">パスワードが一致しません</p>
              )}
              {confirmPassword && newPassword === confirmPassword && (
                <p className="text-xs text-green-500 mt-1">✓ パスワードが一致しています</p>
              )}
            </div>

            {/* 送信ボタン */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={changeMutation.isPending}
                className="btn-primary"
              >
                {changeMutation.isPending ? '変更中...' : 'パスワードを変更'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}

